import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// no router links needed here
import { supabase } from '../supabaseClient';
import AdvancedShareModal from './AdvancedShareModal';
import VerdictMemeFrame from './VerdictMemeFrame';
import { getCanonicalRenderedVerdictAsset } from '../utils/verdictAssets';
import { safeStorage } from '../utils/safeStorage';
import DailyQuestionShareModal from './DailyQuestionShareModal';
import CommunityRulesPanel from './CommunityRulesPanel';
import ReportModal from './ReportModal';
import { DAILY_QUESTION_CANONICAL_SHARE } from '../utils/featureFlags';
import { useDailyDecisionForegroundRefresh, useDailyDecisionShareState } from '../hooks/useDailyDecision';
import { resolveDailyVerdictRenderState } from '../utils/dailyVerdictRender';
import { consumeCrewDailyChallengePending } from '../utils/crewDailyChallenge';
import { recordReferralMilestone } from '../utils/referrals';

// Survives component remounts to prevent startup mount-fetch storms on iOS WebView.
let lastMountFetchStartedAtMs = 0;
const MOUNT_FETCH_MIN_INTERVAL_MS = 1000;
let dailyMountEnsureInFlight = null;
let dailyMountEnsureCache = null; // { ts, data, error }
const DAILY_MOUNT_CACHE_TTL_MS = 30000;
let lastDailyHomeRenderLogKeyGlobal = null;
const DAILY_VOTE_CACHE_KEY = 'daily_decision_vote_cache_v1';

const PENDING_POLL_BACKOFF_STEPS_MS = [2000, 3000, 5000, 8000];
const PENDING_POLL_MAX_WINDOW_MS = 120000;
const SHARE_HYDRATE_RETRY_MS = 6000;
const SHARE_HYDRATE_RETRY_MAX = 4;
const LOAD_WATCHDOG_MS = 12000;
const STATUS_RANK = {
  active: 1,
  open: 1,
  closed_pending_verdict: 2,
  closing: 2,
  closed: 3,
  complete: 3,
  completed: 3,
  verdict_ready: 3,
  revealed: 3,
  archived: 3,
};
const TERMINAL_DAILY_STATUSES = new Set(['closed', 'complete', 'completed', 'verdict_ready', 'revealed', 'archived']);

const getStatusRank = (status) => STATUS_RANK[String(status || '').toLowerCase()] || 0;

function hasDisplayableVerdictPayload(payload) {
  if (!payload || typeof payload !== 'object') return false;
  const winnerIdx = Number(payload.winning_option_index);
  const totalVotes = Number(payload.total_votes);
  const tallies = payload.tallies;
  return Number.isFinite(winnerIdx)
    && Number.isFinite(totalVotes)
    && tallies
    && typeof tallies === 'object';
}

function getEtIsoDate() {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const y = parts.find((p) => p.type === 'year')?.value;
    const m = parts.find((p) => p.type === 'month')?.value;
    const d = parts.find((p) => p.type === 'day')?.value;
    if (y && m && d) return `${y}-${m}-${d}`;
  } catch (_) {}
  return null;
}

function getCurrentDailyDecisionDateEt() {
  const todayEt = getEtIsoDate();
  if (!todayEt) return null;
  try {
    const now = new Date();
    const nowEtText = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(now);
    const [hourRaw, minuteRaw] = String(nowEtText || '').split(':');
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);
    if (Number.isFinite(hour) && Number.isFinite(minute) && (hour > 21 || (hour === 21 && minute >= 0))) {
      const [y, m, d] = todayEt.split('-').map(Number);
      const next = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
      next.setUTCDate(next.getUTCDate() + 1);
      const yy = next.getUTCFullYear();
      const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(next.getUTCDate()).padStart(2, '0');
      return `${yy}-${mm}-${dd}`;
    }
  } catch (_) {}
  return todayEt;
}

function isCurrentCycleHydration(cachedMount) {
  const cachedDecision = cachedMount?.data?.decision || null;
  const cachedState = cachedMount?.data?.state || null;
  const decisionDate = String(cachedDecision?.decision_date || '').trim();
  const currentDecisionDate = getCurrentDailyDecisionDateEt();
  const rawStatus = String(cachedState?.status || cachedDecision?.status || '').toLowerCase();
  const normalizedStatus =
    rawStatus === 'open'
      ? 'active'
      : rawStatus === 'closing'
        ? 'closed_pending_verdict'
        : rawStatus;

  if (!decisionDate || !currentDecisionDate || decisionDate !== currentDecisionDate) {
    return false;
  }

  return ['active', 'closed_pending_verdict'].includes(normalizedStatus);
}

function getFreshMountEnsureCache() {
  if (!dailyMountEnsureCache) return null;
  const age = Date.now() - Number(dailyMountEnsureCache.ts || 0);
  if (age > DAILY_MOUNT_CACHE_TTL_MS) return null;
  if (!isCurrentCycleHydration(dailyMountEnsureCache)) return null;
  return dailyMountEnsureCache;
}

function getHydrationFromCachedMount(cachedMount) {
  const cachedDecision = cachedMount?.data?.decision || null;
  const cachedState = cachedMount?.data?.state || null;
  if (!cachedDecision) return { decision: null, tally: null, result: null };

  const normalizedStatus =
    cachedState?.status === 'open'
      ? 'active'
      : cachedState?.status === 'closing'
        ? 'closed_pending_verdict'
        : (cachedState?.status || cachedDecision?.status || null);

  const decision = {
    ...cachedDecision,
    status: normalizedStatus || cachedDecision?.status || null,
    closes_at: cachedState?.closes_at || cachedDecision?.closes_at || null,
  };

  const tally = (cachedState?.tallies && typeof cachedState.tallies === 'object')
    ? {
        tallies: cachedState.tallies,
        total_votes: Number(cachedState.total_votes || 0),
      }
    : null;

  const result = hasDisplayableVerdictPayload(cachedState?.final_verdict)
    ? {
        decision_id: cachedState.decision_id || cachedDecision?.id || null,
        tallies: cachedState.tallies || {},
        total_votes: Number(cachedState.total_votes || 0),
        final_verdict: cachedState.final_verdict,
        verdict_source: cachedState.verdict_source || null,
        reason_code: cachedState.reason_code || null,
        last_updated_at: cachedState.last_updated_at || null,
        meme_status: cachedState.meme_status || null,
        meme_image_url: cachedState.meme_image_url || null,
        share_card_url: cachedState.share_card_url || null,
        composed_share_card_url: cachedState.composed_share_card_url || null,
        background_image_url: cachedState.background_image_url || null,
        final_image_url: cachedState.final_image_url || null,
        media_asset_url: cachedState.media_asset_url || null,
        meme_snark_text: cachedState.meme_snark_text || null,
        meme_error: cachedState.meme_error || null,
      }
    : null;

  return { decision, tally, result };
}

function readDailyVoteCache() {
  try {
    const raw = safeStorage.get(DAILY_VOTE_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function getCachedDailyVote(decisionId) {
  const id = String(decisionId || '').trim();
  if (!id) return null;
  const cache = readDailyVoteCache();
  const n = Number(cache[id]);
  return Number.isFinite(n) ? n : null;
}

function setCachedDailyVote(decisionId, optionIndex) {
  const id = String(decisionId || '').trim();
  const n = Number(optionIndex);
  if (!id || !Number.isFinite(n)) return;
  try {
    const cache = readDailyVoteCache();
    cache[id] = n;
    const keys = Object.keys(cache);
    if (keys.length > 30) {
      keys.slice(0, keys.length - 30).forEach((k) => delete cache[k]);
    }
    safeStorage.set(DAILY_VOTE_CACHE_KEY, JSON.stringify(cache));
  } catch {}
}

function getDisplayableVerdictPayload(result) {
  if (hasDisplayableVerdictPayload(result?.final_verdict)) return result.final_verdict;
  if (hasDisplayableVerdictPayload(result)) return result;
  return null;
}

function normalizeTallies(tallies, optionCount = 0) {
  const out = {};
  if (tallies && typeof tallies === 'object') {
    Object.entries(tallies).forEach(([key, value]) => {
      const idx = Number(key);
      const count = Number(value);
      if (!Number.isFinite(idx) || idx < 0) return;
      out[idx] = Math.max(0, Number.isFinite(count) ? count : 0);
    });
  }
  for (let i = 0; i < optionCount; i += 1) {
    if (!Number.isFinite(Number(out[i]))) out[i] = 0;
  }
  return out;
}

function buildRenderableVerdict({ status, result, tally, options }) {
  const payload = getDisplayableVerdictPayload(result);
  const statusKey = String(status || '').toLowerCase();
  const optionList = Array.isArray(options) ? options : [];
  if (payload) {
    const normalizedTallies = normalizeTallies(payload.tallies, optionList.length);
    const totalVotes = Number(payload.total_votes ?? Object.values(normalizedTallies).reduce((a, b) => a + Number(b || 0), 0));
    const winnerIdx = Number.isFinite(Number(payload.winning_option_index)) ? Number(payload.winning_option_index) : 0;
    return {
      ready: true,
      source: 'payload',
      model: {
        winning_option_index: winnerIdx,
        winning_option: optionList[winnerIdx] || payload.winning_option || `Option ${winnerIdx + 1}`,
        tallies: normalizedTallies,
        total_votes: totalVotes,
        verdict_text: payload.verdict_text || `${optionList[winnerIdx] || `Option ${winnerIdx + 1}`} won with ${totalVotes} vote${totalVotes === 1 ? '' : 's'}.`,
      },
    };
  }

  const terminal = TERMINAL_DAILY_STATUSES.has(statusKey) || statusKey === 'closed_pending_verdict' || statusKey === 'closing';
  const normalizedTallies = normalizeTallies(result?.tallies || tally?.tallies, optionList.length);
  const totalVotes = Number(result?.total_votes ?? tally?.total_votes ?? Object.values(normalizedTallies).reduce((a, b) => a + Number(b || 0), 0));
  const hasTallies = Object.keys(normalizedTallies).length > 0;
  const explicitWinner = Number(result?.winning_option_index);
  const ranked = Object.entries(normalizedTallies)
    .map(([idx, count]) => ({ idx: Number(idx), count: Number(count || 0) }))
    .filter((r) => Number.isFinite(r.idx))
    .sort((a, b) => (b.count - a.count) || (a.idx - b.idx));
  const winnerIdx = Number.isFinite(explicitWinner) ? explicitWinner : (ranked[0]?.idx ?? 0);
  const canRender = terminal && (Number.isFinite(explicitWinner) || hasTallies || totalVotes >= 1);
  if (!canRender) return { ready: false, source: null, model: null };

  return {
    ready: true,
    source: 'derived',
    model: {
      winning_option_index: winnerIdx,
      winning_option: optionList[winnerIdx] || `Option ${winnerIdx + 1}`,
      tallies: normalizedTallies,
      total_votes: totalVotes,
      verdict_text: totalVotes === 0
        ? 'No votes were cast. Defaulting to option 1.'
        : `${optionList[winnerIdx] || `Option ${winnerIdx + 1}`} won with ${totalVotes} vote${totalVotes === 1 ? '' : 's'}.`,
    },
  };
}

const formatEtTime = (iso) => {
  try {
    const dt = new Date(iso);
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(dt);
  } catch {
    return '9:00 PM ET';
  }
};

const formatLastUpdated = (ts) => {
  if (!ts) return null;
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit'
    }).format(new Date(ts));
  } catch {
    return null;
  }
};

const getCountdown = (iso, nowTs) => {
  if (!iso) return '-';
  const closeAt = new Date(iso).getTime();
  const now = nowTs || Date.now();
  const diff = Math.max(0, closeAt - now);
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs <= 0) return `${rem}m`;
  return `${hrs}h ${rem}m`;
};

export default function DailyDecisionVote({ authUser, userId, onRequireAuth, onCrewChallengeVoteSuccess, homeHeroMode = false }) {
  const initialMountHydration = getHydrationFromCachedMount(getFreshMountEnsureCache());
  const initialCachedVote = getCachedDailyVote(initialMountHydration.decision?.id);
  const [decision, setDecision] = useState(() => initialMountHydration.decision);
  const [loading, setLoading] = useState(() => !initialMountHydration.decision);
  const [error, setError] = useState(null);
  const [userVote, setUserVote] = useState(() => initialCachedVote);
  const [tally, setTally] = useState(() => initialMountHydration.tally);
  const [result, setResult] = useState(() => initialMountHydration.result);
  const [sharedCrewIds, setSharedCrewIds] = useState(new Set());
  const [allCrews, setAllCrews] = useState([]);
  const [shareToast, setShareToast] = useState(null);
  const [appUserId, setAppUserId] = useState(userId || null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [voteSubmitting, setVoteSubmitting] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('spam');
  const [reportNote, setReportNote] = useState('');
  // [DAILY_DECISION_VIEWMODE]
  const [viewMode, setViewMode] = useState(() => (initialCachedVote != null ? 'leaning' : 'vote')); // vote | leaning
  const [showVerdictToast, setShowVerdictToast] = useState(false);
  const prevStatusRef = useRef(null);
  const toastTimerRef = useRef(null);
  const lastToastKeyRef = useRef(null);
  const verdictPollRef = useRef(null);
  const verdictPollAttemptRef = useRef(0);
  const [verdictPendingSince, setVerdictPendingSince] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [verdictShareOpen, setVerdictShareOpen] = useState(false);
  const [shareBlob, setShareBlob] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [crewPulse, setCrewPulse] = useState(null);
  const refreshInFlightRef = useRef(false);
  const mountLoadInFlightRef = useRef(false);
  const mountLoadRequestedRef = useRef(false);
  const loadRequestTokenRef = useRef(0);
  const loadRequestMetaRef = useRef(new Map());
  const loadingTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const decisionRef = useRef(null);
  const voteResolutionRef = useRef({}); // decisionId -> 'unknown' | 'present' | 'none'
  const userVoteRef = useRef(initialCachedVote);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(() => Boolean(initialMountHydration.decision));
  const hasLoadedOnceRef = useRef(Boolean(initialMountHydration.decision));
  const shareHydrateRetryRef = useRef(null);
  const shareHydrateRetryCountRef = useRef(0);
  const dailyCountersRef = useRef({
    stale_response_dropped: 0,
    authoritative_state_missing: 0,
  });
  const lastRenderLogKeyRef = useRef(null);
  const memeRefreshAttemptsRef = useRef(0);
  const memeRefreshTimerRef = useRef(null);
  const loadDecisionRef = useRef(null);
  const isNativePlatform = useMemo(() => {
    try {
      return Boolean(window?.Capacitor?.isNativePlatform?.());
    } catch {
      return false;
    }
  }, []);
  const isNativeInitialMount = isNativePlatform && !hasLoadedOnce;
  const crewIdFromUrl = (() => {
    try {
      return new URLSearchParams(window.location.search).get('crew');
    } catch {
      return null;
    }
  })();
  const crewContext = useMemo(() => {
    if (!crewIdFromUrl) return null;
    return allCrews.find((c) => c.id === crewIdFromUrl) || null;
  }, [allCrews, crewIdFromUrl]);

  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    decisionRef.current = decision;
  }, [decision]);

  useEffect(() => {
    userVoteRef.current = userVote;
  }, [userVote]);

  const beginRequestToken = useCallback((reason = 'unknown') => {
    const token = ++loadRequestTokenRef.current;
    const requestId = `daily_${token}`;
    loadRequestMetaRef.current.set(token, { requestId, staleLogged: false, reason });
    return token;
  }, []);
  const getRequestIdForToken = useCallback((token) => {
    const meta = loadRequestMetaRef.current.get(token);
    return meta?.requestId || `daily_${token}`;
  }, []);
  const isTokenCurrent = useCallback(
    (token) => {
      const isCurrent = isMountedRef.current && loadRequestTokenRef.current === token;
      if (!isCurrent) {
        const meta = loadRequestMetaRef.current.get(token);
        if (meta && !meta.staleLogged) {
          meta.staleLogged = true;
          loadRequestMetaRef.current.set(token, meta);
        }
      }
      return isCurrent;
    },
    []
  );

  // [DAILY_LOAD_TIMEOUT_CLEANUP]
  const clearLoadingTimeout = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (userId) {
      setAppUserId(userId);
      return;
    }
    const loadAppUserId = async () => {
      if (!authUser?.id) return;
      const { data } = await supabase
        .from('users')
        .select('id')
        .eq('auth_user_id', authUser.id)
        .maybeSingle();
      if (data?.id) setAppUserId(data.id);
    };
    loadAppUserId();
  }, [userId, authUser?.id]);

  const loadShareState = useCallback(async (decisionId, appUid, authUid, reason = 'manual', requestToken = null) => {
    // [DAILY_DECISION_SHARE_STATE_LOAD]
    if (!decisionId || (!appUid && !authUid)) return;
    if (requestToken != null && !isTokenCurrent(requestToken)) return;
    if (reason !== 'retry') {
      shareHydrateRetryCountRef.current = 0;
    }
    try {
      if (requestToken != null && !isTokenCurrent(requestToken)) return;
      console.log('[daily] share_state_hydrate_start', {
        decision_id: decisionId,
        app_user_id: appUid || null,
        auth_user_id: authUid || null,
        reason,
      });
      const { data, error: fnErr } = await supabase.functions.invoke('share-daily-question', {
        body: { daily_question_id: decisionId, mode: 'status' },
      });
      if (fnErr) throw fnErr;
      if (requestToken != null && !isTokenCurrent(requestToken)) return;
      const crewList = data?.allCrews || [];
      const sharedSet = new Set(data?.sharedCrewIds || []);
      if (requestToken != null && !isTokenCurrent(requestToken)) return;
      setAllCrews(crewList);
      // [DAILY_SHARE_IMMUTABLE_SET]
      // Always commit a fresh Set instance so React state/memo dependencies stay reactive.
      setSharedCrewIds(sharedSet);
      if (requestToken != null && !isTokenCurrent(requestToken)) return;
      console.log('[daily] share_state_hydrate_end', {
        decision_id: decisionId,
        crews: crewList.length,
        shared: sharedSet.size,
        reason,
      });
      shareHydrateRetryCountRef.current = 0;
      if (shareHydrateRetryRef.current) {
        clearTimeout(shareHydrateRetryRef.current);
        shareHydrateRetryRef.current = null;
      }
    } catch (err) {
      if (requestToken != null && !isTokenCurrent(requestToken)) return;
      console.warn('[daily] share_state_hydrate_error', { decision_id: decisionId, err: err?.message || err });
      // [DAILY_SHARE_HYDRATION_FALLBACK]
      // Keep last-known-good share state on transient failures; never reset to empty on error.
      if (shareHydrateRetryRef.current) clearTimeout(shareHydrateRetryRef.current);
      const nextAttempt = shareHydrateRetryCountRef.current + 1;
      if (nextAttempt > SHARE_HYDRATE_RETRY_MAX) {
        console.warn('[daily] share_state_hydrate_retry_exhausted', {
          decision_id: decisionId,
          max_retries: SHARE_HYDRATE_RETRY_MAX,
        });
        return;
      }
      shareHydrateRetryCountRef.current = nextAttempt;
      const delayMs = Math.min(SHARE_HYDRATE_RETRY_MS * (2 ** (nextAttempt - 1)), 30000);
      shareHydrateRetryRef.current = setTimeout(() => {
        console.log('[daily] share_state_hydrate_fail_retry', {
          decision_id: decisionId,
          retry_in_ms: delayMs,
          attempt: nextAttempt,
        });
        loadShareState(decisionId, appUid, authUid, 'retry', requestToken);
      }, delayMs);
    }
  }, [isTokenCurrent]);

  useEffect(() => {
    const currentStatus = decision?.status || null;
    const prevStatus = prevStatusRef.current;

    const storedId = safeStorage.get('daily_decision_last_id');
    const storedStatus = safeStorage.get('daily_decision_last_status');

    const verdictReady = buildRenderableVerdict({
      status: currentStatus,
      result,
      tally,
      options: decision?.options || [],
    }).ready;
    const shouldToast =
      decision?.id &&
      currentStatus === 'verdict_ready' &&
      verdictReady &&
      (
        (prevStatus === 'open') ||
        (storedId === decision.id && storedStatus === 'open')
      );

    if (shouldToast) {
      const toastKey = `${decision.id}:${currentStatus}`;
      if (lastToastKeyRef.current !== toastKey) {
        setShowVerdictToast(true);
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => {
          setShowVerdictToast(false);
        }, 5000);
        lastToastKeyRef.current = toastKey;
      }
    }

    if (decision?.id && currentStatus) {
      safeStorage.set('daily_decision_last_id', decision.id);
      safeStorage.set('daily_decision_last_status', currentStatus);
    }

    prevStatusRef.current = currentStatus;
    return () => {};
  }, [decision?.id, decision?.status, decision?.options, result, tally]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearLoadingTimeout();
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (shareHydrateRetryRef.current) {
        clearTimeout(shareHydrateRetryRef.current);
        shareHydrateRetryRef.current = null;
      }
      if (memeRefreshTimerRef.current) {
        clearTimeout(memeRefreshTimerRef.current);
        memeRefreshTimerRef.current = null;
      }
      shareHydrateRetryCountRef.current = 0;
    };
  }, [clearLoadingTimeout]);

  // [HOME_DAILY_RENDER_DECISION_LOG]
  useEffect(() => {
    const logPayload = {
      decision_id: decision?.id || null,
      status: decision?.status || null,
      source: decision?.id ? 'server_or_fallback' : 'none',
      has_error: Boolean(error),
    };
    const logKey = JSON.stringify(logPayload);
    if (lastDailyHomeRenderLogKeyGlobal === logKey) return;
    lastDailyHomeRenderLogKeyGlobal = logKey;
  }, [decision?.id, decision?.status, error]);

  const loadResult = useCallback(async (decisionId, requestToken = null) => {
    try {
      const { data: resRow } = await supabase
        .from('daily_decision_results')
        .select('*')
        .eq('decision_id', decisionId)
        .maybeSingle();
      if (requestToken != null && !isTokenCurrent(requestToken)) return null;
      setResult((prev) => {
        if (!resRow && prev && typeof prev.winning_option_index === 'number') {
          dailyCountersRef.current.stale_response_dropped += 1;
          console.warn('[daily] stale_response_dropped', {
            decision_id: decisionId,
            prev_status: 'verdict_ready',
            next_status: 'result_null',
            dropped_count: dailyCountersRef.current.stale_response_dropped,
          });
          return prev;
        }
        return resRow || null;
      });
      if (requestToken != null && !isTokenCurrent(requestToken)) return null;
      if (resRow?.decision_id) {
        const renderState = resolveDailyVerdictRenderState(resRow, decisionRef.current);
        if (renderState.verdictReady && decisionRef.current?.status) {
          const statusKey = String(decisionRef.current.status || '').toLowerCase();
          if (statusKey === 'active' || statusKey === 'open') {
            setDecision((prev) => prev ? { ...prev, status: 'verdict_ready' } : prev);
          }
        }
      }
      setLastUpdatedAt(new Date().toISOString());
      return resRow || null;
    } catch (err) {
      if (requestToken != null && !isTokenCurrent(requestToken)) return null;
      console.error('[DailyDecision] result load error:', err);
      setResult(null);
      if (requestToken != null && !isTokenCurrent(requestToken)) return null;
      setLastUpdatedAt(new Date().toISOString());
      return null;
    }
  }, [isTokenCurrent]);

  // [HOME_DAILY_DECISION_FALLBACK_FETCH]
  // [DAILY_HOME_VISIBILITY_STABILIZATION]
  const loadDecisionFallback = useCallback(async () => {
    const currentDecisionDateEt = getCurrentDailyDecisionDateEt();
    let row = null;

    if (currentDecisionDateEt) {
      const { data: currentRow } = await supabase
        .from('daily_decisions')
        .select('*')
        .eq('decision_date', currentDecisionDateEt)
        .in('status', ['active', 'open', 'closed_pending_verdict'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      row = currentRow || null;
    }

    if (!row) {
      const { data: latestRow } = await supabase
        .from('daily_decisions')
        .select('*')
        .in('status', ['active', 'open', 'closed_pending_verdict'])
        .order('decision_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      row = latestRow || null;
    }

    return row;
  }, []);

  const loadDecision = useCallback(async ({ reason = 'manual', silent = false } = {}) => {
    if (reason === 'mount') {
      const nowMs = Date.now();
      if (nowMs - lastMountFetchStartedAtMs < MOUNT_FETCH_MIN_INTERVAL_MS) {
        return;
      }
      lastMountFetchStartedAtMs = nowMs;
    }
    // [DAILY_MOUNT_DEDUP]
    if (reason === 'mount' && mountLoadInFlightRef.current) {
      return;
    }
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    if (reason === 'mount') mountLoadInFlightRef.current = true;
    // [DAILY_LOAD_REQUEST_TOKEN_GUARD]
    const requestToken = beginRequestToken(reason);
    const requestId = getRequestIdForToken(requestToken);
    const shouldLogLoad = reason === 'mount' || !silent;
    const shouldClearLoadingOnSilent = silent && loading && !decisionRef.current;
    const startedAt = Date.now();
    if (shouldLogLoad) {
      console.log('[daily] load_start', { request_id: requestId, reason, silent });
    }
    if (!silent && isTokenCurrent(requestToken)) setLoading(true);
    if (!silent && isTokenCurrent(requestToken)) setError(null);
    clearLoadingTimeout();
    loadingTimeoutRef.current = setTimeout(() => {
      if (!isTokenCurrent(requestToken)) return;
      console.warn('[daily] fetch_timeout_soft', { request_id: requestId, reason, timeout_ms: LOAD_WATCHDOG_MS });
      if (!silent) setLoading(false);
    }, LOAD_WATCHDOG_MS);
    try {
      let data;
      let fnErr;
      if (reason === 'mount') {
        const nowMs = Date.now();
        const cached = getFreshMountEnsureCache();
        if (cached && (nowMs - cached.ts) < DAILY_MOUNT_CACHE_TTL_MS) {
          data = cached.data;
          fnErr = cached.error;
        } else {
          if (!dailyMountEnsureInFlight) {
            dailyMountEnsureInFlight = supabase.functions.invoke('ensure-daily-decision', { body: {} })
              .then((res) => {
                dailyMountEnsureCache = { ts: Date.now(), data: res?.data || null, error: res?.error || null };
                return res;
              })
              .finally(() => {
                dailyMountEnsureInFlight = null;
              });
          }
          const sharedRes = await dailyMountEnsureInFlight;
          data = sharedRes?.data || null;
          fnErr = sharedRes?.error || null;
        }
      } else {
        const res = await supabase.functions.invoke('ensure-daily-decision', { body: {} });
        data = res?.data || null;
        fnErr = res?.error || null;
      }
      if (!isTokenCurrent(requestToken)) return;
      let row = data?.decision || null;
      let authoritativeState = data?.state || null;
      const debugState = data?.debug || null;
      if (fnErr) {
        console.warn('[DailyDecision] ensure failed, using fallback:', fnErr?.message || fnErr);
        row = await loadDecisionFallback();
        if (row?.id) {
          const { data: fallbackState } = await supabase.rpc('get_daily_decision_state', {
            p_decision_id: row.id,
            p_now: new Date().toISOString(),
            p_finalize: true,
          }).maybeSingle();
          authoritativeState = fallbackState || null;
        }
      }
      if (!row) {
        row = await loadDecisionFallback();
        if (row?.id) {
          const { data: fallbackState } = await supabase.rpc('get_daily_decision_state', {
            p_decision_id: row.id,
            p_now: new Date().toISOString(),
            p_finalize: true,
          }).maybeSingle();
          authoritativeState = fallbackState || null;
        }
      }
      if (!isTokenCurrent(requestToken)) return;
      if (authoritativeState?.status) {
        const normalizedStatus =
          authoritativeState.status === 'open'
            ? 'active'
            : authoritativeState.status === 'closing'
              ? 'closed_pending_verdict'
              : authoritativeState.status;
        row = {
          ...row,
          status: normalizedStatus,
          closes_at: authoritativeState.closes_at || row?.closes_at || null,
        };
      }
      const previousDecisionId = decisionRef.current?.id || null;
      if (row?.id && previousDecisionId && row.id !== previousDecisionId) {
        setResult(null);
        setTally(null);
      }
      if (row?.id) {
        setDecision((prev) => {
          if (!prev || prev.id !== row.id) return row;
          const prevRank = getStatusRank(prev.status);
          const nextRank = getStatusRank(row.status);
          if (nextRank < prevRank) {
            dailyCountersRef.current.stale_response_dropped += 1;
            console.warn('[daily] stale_response_dropped', {
              decision_id: row.id,
              prev_status: prev.status || null,
              next_status: row.status || null,
              dropped_count: dailyCountersRef.current.stale_response_dropped,
            });
            return { ...row, status: prev.status, closes_at: prev.closes_at || row.closes_at };
          }
          return row;
        });
      } else {
        setDecision(row || null);
      }
      clearLoadingTimeout();
      if (!hasLoadedOnceRef.current) {
        hasLoadedOnceRef.current = true;
        setHasLoadedOnce(true);
      }

      if (authUser?.id && row?.id) {
        const decisionIdKey = String(row.id);
        const canLookupVote = Boolean(authUser?.id && row?.id);
        const { data: voteRow } = await supabase
          .from('daily_decision_votes')
          .select('option_index')
          .eq('decision_id', row.id)
          .eq('user_id', authUser.id)
          .maybeSingle();
        if (!isTokenCurrent(requestToken)) return;
        const normalizedVote = voteRow?.option_index != null ? Number(voteRow.option_index) : null;
        const nextVote = Number.isFinite(normalizedVote) ? normalizedVote : null;
        const cachedVote = getCachedDailyVote(row?.id);
        const currentDecisionId = decisionRef.current?.id || null;
        const hasLocalVote = userVoteRef.current != null || cachedVote != null;
        const shouldPreserveKnownVote = nextVote == null && currentDecisionId && row?.id && currentDecisionId === row.id && hasLocalVote;
        if (decisionIdKey) {
          if (nextVote != null) {
            voteResolutionRef.current[decisionIdKey] = 'present';
          } else if (canLookupVote) {
            if (!hasLocalVote) {
              voteResolutionRef.current[decisionIdKey] = 'none';
            } else if (!voteResolutionRef.current[decisionIdKey]) {
              voteResolutionRef.current[decisionIdKey] = 'present';
            }
          } else if (!voteResolutionRef.current[decisionIdKey]) {
            voteResolutionRef.current[decisionIdKey] = 'unknown';
          }
        }
        setUserVote((prev) => {
          const resolution = voteResolutionRef.current[decisionIdKey] || 'unknown';
          const resolved =
            nextVote != null
              ? nextVote
              : shouldPreserveKnownVote && prev != null
                ? prev
                : (resolution === 'none' ? null : cachedVote);
          if (process.env.NODE_ENV !== 'production' && prev !== resolved) {
            console.log('[daily] voted_state_transition', {
              from: prev,
              to: resolved,
              reason:
                nextVote != null
                  ? 'server_vote_hydrate'
                  : shouldPreserveKnownVote
                    ? 'preserve_known_vote_on_null_refresh'
                    : (resolution === 'none'
                      ? 'server_vote_explicit_none'
                      : (cachedVote != null ? 'cache_vote_hydrate' : 'server_vote_unknown')),
              decisionId: row?.id || null,
            });
          }
          if (resolved != null) setCachedDailyVote(row?.id, resolved);
          return resolved;
        });
        setViewMode((prevMode) => {
          const resolution = voteResolutionRef.current[decisionIdKey] || 'unknown';
          const resolvedVote =
            nextVote != null
              ? nextVote
              : shouldPreserveKnownVote && userVoteRef.current != null
                ? userVoteRef.current
                : (resolution === 'none' ? null : cachedVote);
          const nextMode = resolvedVote != null ? 'leaning' : 'vote';
          if (process.env.NODE_ENV !== 'production' && prevMode !== nextMode) {
            console.log('[daily] voted_state_transition', {
              from: prevMode,
              to: nextMode,
              reason: 'view_mode_from_vote_hydrate',
              decisionId: row?.id || null,
            });
          }
          return nextMode;
        });
      } else {
        const currentDecisionId = decisionRef.current?.id ? String(decisionRef.current.id) : '';
        const resolution = currentDecisionId ? (voteResolutionRef.current[currentDecisionId] || 'unknown') : 'unknown';
        setViewMode((prevMode) => ((userVoteRef.current != null || resolution !== 'none') ? prevMode : 'vote'));
      }

      if (authoritativeState?.decision_id) {
        const normalizedTallies =
          authoritativeState.tallies && typeof authoritativeState.tallies === 'object'
            ? authoritativeState.tallies
            : {};
        const normalizedTotalVotes = Number(authoritativeState.total_votes || 0);
        const debugLiveTallies =
          debugState?.live_tallies && typeof debugState.live_tallies === 'object'
            ? debugState.live_tallies
            : null;
        const debugLiveVotes = Number(debugState?.live_total_votes ?? 0);
        const effectiveTallies = debugLiveVotes > normalizedTotalVotes && debugLiveTallies
          ? debugLiveTallies
          : normalizedTallies;
        const effectiveTotalVotes = debugLiveVotes > normalizedTotalVotes
          ? debugLiveVotes
          : normalizedTotalVotes;
        const nextStatusKey = String(row?.status || '').toLowerCase();
        if (isTokenCurrent(requestToken)) {
          setTally({
            tallies: effectiveTallies,
            total_votes: effectiveTotalVotes,
          });
        }
        const hasVerdictPayload = hasDisplayableVerdictPayload(authoritativeState.final_verdict);
        if (
          hasVerdictPayload
        ) {
          const authoritativeResult = {
            decision_id: authoritativeState.decision_id,
            tallies: effectiveTallies,
            total_votes: effectiveTotalVotes,
            winning_option_index: Number(
              authoritativeState.final_verdict.winning_option_index ?? 0
            ),
            final_verdict: authoritativeState.final_verdict,
            verdict_source: authoritativeState.verdict_source || null,
            reason_code: authoritativeState.reason_code || null,
            last_updated_at: authoritativeState.last_updated_at || null,
            meme_status: authoritativeState.meme_status || null,
            meme_image_url: authoritativeState.meme_image_url || null,
            share_card_url: authoritativeState.share_card_url || null,
            composed_share_card_url: authoritativeState.composed_share_card_url || null,
            background_image_url: authoritativeState.background_image_url || null,
            final_image_url: authoritativeState.final_image_url || null,
            media_asset_url: authoritativeState.media_asset_url || null,
            meme_snark_text: authoritativeState.meme_snark_text || null,
            meme_error: authoritativeState.meme_error || null,
          };
          if (isTokenCurrent(requestToken)) {
            setResult(authoritativeResult);
          }
        } else if (TERMINAL_DAILY_STATUSES.has(nextStatusKey)) {
          console.warn('[daily] terminal_without_authoritative_final_verdict', {
            decision_id: authoritativeState.decision_id || row?.id || null,
            status: row?.status || null,
            verdict_source: authoritativeState.verdict_source || null,
          });
          if (isTokenCurrent(requestToken)) {
            setResult((prev) => (hasDisplayableVerdictPayload(prev?.final_verdict) ? prev : null));
          }
        } else if (row?.id && row?.status !== 'active' && row?.status !== 'open') {
          await loadResult(row.id, requestToken);
        } else if (isTokenCurrent(requestToken)) {
          setResult(null);
        }
      } else if (row?.id && row?.status !== 'active' && row?.status !== 'open') {
        await loadResult(row.id, requestToken);
      } else if (row?.id) {
        const { data: tallyData } = await supabase
          .rpc('get_daily_decision_tally_cached', { p_decision_id: row.id });
        if (isTokenCurrent(requestToken) && tallyData) setTally(tallyData);
      }
      if (row?.id && DAILY_QUESTION_CANONICAL_SHARE) {
        await loadShareState(row.id, appUserId, authUser?.id, reason, requestToken);
      }

      if (row?.id) {
        await loadResult(row.id, requestToken);
      }

      if (!isTokenCurrent(requestToken)) return;
      setLastUpdatedAt(new Date().toISOString());
      if (shouldLogLoad) {
        console.log('[daily] load_success', {
          request_id: requestId,
          reason,
          decision_id: row?.id || null,
          status: row?.status || null,
          elapsed_ms: Date.now() - startedAt,
        });
      }
    } catch (err) {
      console.error('[DailyDecision] load error:', err);
      if (shouldLogLoad) {
        console.warn('[daily] load_failure', {
          request_id: requestId,
          reason,
          error: err?.message || String(err),
          elapsed_ms: Date.now() - startedAt,
        });
      }
      const fallbackRow = await loadDecisionFallback();
      if (!isTokenCurrent(requestToken)) return;
      if (fallbackRow) {
        setDecision(fallbackRow);
        clearLoadingTimeout();
        if (!hasLoadedOnceRef.current) {
          hasLoadedOnceRef.current = true;
          setHasLoadedOnce(true);
        }
      } else if (!silent) {
        if (!decisionRef.current) setError('Unable to load today\'s decision');
      }
      setLastUpdatedAt(new Date().toISOString());
    } finally {
      if (isTokenCurrent(requestToken)) {
        clearLoadingTimeout();
        if (!silent || shouldClearLoadingOnSilent) setLoading(false);
        if (shouldLogLoad) {
          console.log('[daily] load_cleared', {
            request_id: requestId,
            reason,
            silent,
            cleared: !silent || shouldClearLoadingOnSilent,
          });
        }
      }
      refreshInFlightRef.current = false;
      if (reason === 'mount') mountLoadInFlightRef.current = false;
    }
  }, [authUser?.id, loadResult, loadDecisionFallback, loadShareState, appUserId, clearLoadingTimeout, beginRequestToken, getRequestIdForToken, isTokenCurrent, loading]);

  loadDecisionRef.current = loadDecision;

  useEffect(() => {
    // Run mount hydration/load once per component mount. Do not depend on callback identity.
    if (mountLoadRequestedRef.current) return;
    mountLoadRequestedRef.current = true;
    // Initial state already hydrates from cache at render time. Avoid redundant mount-time cache
    // rehydration work here because remount churn on iOS can repeatedly trigger this effect.
    if (initialMountHydration.decision?.id) return;
    loadDecisionRef.current?.({ reason: 'mount' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // [DAILY_DECISION_REFRESH_ORCHESTRATION]
  useDailyDecisionForegroundRefresh(loadDecision, 4000);

  useEffect(() => {
    if (!decision?.id || !crewIdFromUrl) return;
    const loadPulse = async () => {
      const { data } = await supabase.functions.invoke('get-daily-question-pulse', {
        body: { daily_question_id: decision.id, crew_id: crewIdFromUrl },
      });
      if (data?.ok) setCrewPulse(data);
    };
    loadPulse();
  }, [decision?.id, crewIdFromUrl]);

  // [DAILY_DECISION_POLL_PENDING]
  useEffect(() => {
    if (!decision?.id) return;
    const verdictReady = resolveDailyVerdictRenderState(result, decision).verdictReady
      || buildRenderableVerdict({
        status: decision?.status,
        result,
        tally,
        options: decision?.options || [],
      }).ready;
    const statusKey = String(decision.status || '').toLowerCase();
    const shouldPoll = !verdictReady && (
      statusKey === 'closed_pending_verdict'
      || statusKey === 'closing'
      || TERMINAL_DAILY_STATUSES.has(statusKey)
    );

    if (shouldPoll) {
      const pendingSince = verdictPendingSince || Date.now();
      if (!verdictPendingSince) setVerdictPendingSince(pendingSince);
      if (Date.now() - pendingSince > PENDING_POLL_MAX_WINDOW_MS) {
        if (verdictPollRef.current) clearTimeout(verdictPollRef.current);
        verdictPollRef.current = null;
        return;
      }

      if (verdictPollRef.current) clearTimeout(verdictPollRef.current);
      const backoffIdx = Math.min(verdictPollAttemptRef.current, PENDING_POLL_BACKOFF_STEPS_MS.length - 1);
      const nextDelay = PENDING_POLL_BACKOFF_STEPS_MS[backoffIdx];
      verdictPollRef.current = setTimeout(async () => {
        verdictPollAttemptRef.current += 1;
        await loadDecision({ reason: 'pending_poll', silent: true });
      }, nextDelay);
    } else {
      setVerdictPendingSince(null);
      verdictPollAttemptRef.current = 0;
      if (verdictPollRef.current) {
        clearTimeout(verdictPollRef.current);
        verdictPollRef.current = null;
      }
    }

    return () => {
      if (verdictPollRef.current) {
        clearTimeout(verdictPollRef.current);
        verdictPollRef.current = null;
      }
    };
  }, [decision?.id, decision?.status, decision?.closes_at, decision?.options, result, tally, loadDecision, loadResult, verdictPendingSince]);

  // [DAILY_DECISION_STATE_GUARD]
  const dailyRenderState = useMemo(
    () => resolveDailyVerdictRenderState(result, decision),
    [result, decision?.id, decision?.status, decision?.closes_at]
  );
  const isOpen =
    !dailyRenderState.verdictReady &&
    (decision?.status === 'active' || decision?.status === 'open') &&
    new Date(decision?.closes_at).getTime() > Date.now();
  const closesLabel = decision?.closes_at ? `${formatEtTime(decision.closes_at)} ET` : '9:00 PM ET';
  const countdown = getCountdown(decision?.closes_at, nowTick);

  const totalVotes = useMemo(() => {
    if (!tally?.total_votes) return 0;
    return tally.total_votes;
  }, [tally]);

  const verdictRender = useMemo(() => buildRenderableVerdict({
    status: decision?.status,
    result,
    tally,
    options: decision?.options || [],
  }), [decision?.status, decision?.options, result, tally]);
  const verdictPayload = verdictRender.model;
  const verdictReady = dailyRenderState.verdictReady || verdictRender.ready;
  const closedPending = !isOpen && !verdictReady;
  const pendingDelayed = !!verdictPendingSince && (Date.now() - verdictPendingSince) >= 120000;
  const { allShared } = useDailyDecisionShareState(allCrews, sharedCrewIds);

  const handleVote = async (idx) => {
    if (!authUser?.id) {
      onRequireAuth?.();
      return;
    }
    if (!isOpen) return;
    if (voteSubmitting) return;

    try {
      setVoteSubmitting(true);
      const { data: voteResp, error: voteErr } = await supabase.functions.invoke('cast-daily-vote', {
        body: {
          decision_id: decision.id,
          option_index: Number(idx),
          crew_id: null,
        },
      });
      if (voteErr || voteResp?.ok === false) {
        const rawError = voteErr?.message || voteResp?.error || 'Vote failed';
        const isClosedError = /voting closed|closed/i.test(String(rawError || ''));
        console.warn('[daily] vote_submit_not_confirmed', {
          decision_id: decision.id,
          option_index: idx,
          error: rawError,
          closed: isClosedError,
        });

        setError(isClosedError ? 'Voting has closed for today.' : 'Could not confirm your vote yet. Refreshing...');
        await loadDecision({ reason: 'vote_submit_not_confirmed', silent: true });

        // If the vote actually landed, clear the warning and keep the user in leaning mode.
        const { data: verifyVote } = await supabase
          .from('daily_decision_votes')
          .select('option_index')
          .eq('decision_id', decision.id)
          .eq('user_id', authUser.id)
          .maybeSingle();
        const recordedIdx = verifyVote?.option_index != null ? Number(verifyVote.option_index) : null;
        if (Number.isFinite(recordedIdx)) {
          setUserVote(recordedIdx);
          setCachedDailyVote(decision.id, recordedIdx);
          voteResolutionRef.current[String(decision.id)] = 'present';
          setViewMode('leaning');
          setError(null);
        }
        return;
      }
      if (window.trackEvent) {
        window.trackEvent('dq_vote_submitted', {
          decision_id: decision.id,
          option_index: idx,
          source: 'home',
          dedupe_key: `dq_vote_submitted:${decision.id}:${authUser.id}:${idx}`,
        });
      }
      try {
        await recordReferralMilestone('first_vote_completed');
      } catch (milestoneErr) {
        console.warn('⚠️ Failed to record referral first_vote_completed milestone:', milestoneErr?.message || milestoneErr);
      }
      setUserVote(Number(idx));
      setCachedDailyVote(decision.id, Number(idx));
      voteResolutionRef.current[String(decision.id)] = 'present';
      setViewMode('leaning');
      if (voteResp?.tally) {
        setTally({
          tallies: voteResp.tally.tallies || {},
          total_votes: Number(voteResp.tally.total_votes || 0),
        });
      }
      if (voteResp?.state?.final_verdict && hasDisplayableVerdictPayload(voteResp.state.final_verdict)) {
        setResult((prev) => ({
          ...(prev || {}),
          decision_id: decision.id,
          tallies: voteResp.state.tallies || voteResp.tally?.tallies || {},
          total_votes: Number(voteResp.state.total_votes || voteResp.tally?.total_votes || 0),
          final_verdict: voteResp.state.final_verdict,
          verdict_source: voteResp.state.verdict_source || null,
          reason_code: voteResp.state.reason_code || null,
          last_updated_at: voteResp.state.last_updated_at || null,
        }));
      }
      await loadDecision({ reason: 'vote_cast', silent: true });
      const pendingCrewChallenge = consumeCrewDailyChallengePending();
      if (pendingCrewChallenge?.crew_name) {
        alert(`✅ You helped keep ${pendingCrewChallenge.crew_name} alive today.`);
        window.dispatchEvent(new Event('crew-challenge-vote-success'));
      }
      if (DAILY_QUESTION_CANONICAL_SHARE) {
        setShareModalOpen(true);
      }
    } catch (err) {
      console.error('[DailyDecision] vote error:', err);
      setError('Could not submit your vote. Please try again.');
    } finally {
      setVoteSubmitting(false);
    }
  };

  const handleMemeRetry = async () => {
    if (!decision?.id) return;
    try {
      await supabase.functions.invoke('regen-daily-decision-meme', {
        body: { decision_id: decision.id },
      });
      await loadResult(decision.id);
    } catch (err) {
      console.error('[DailyDecision] meme retry error:', err);
    }
  };

  const handleShare = async () => {
    const canonicalAsset = getCanonicalRenderedVerdictAsset({ ...(result || {}), ...(decision || {}) });
    if (!canonicalAsset.url || !decision) return;
    try {
      const resp = await fetch(canonicalAsset.url);
      const blob = await resp.blob();
      setShareBlob(blob);
      setVerdictShareOpen(true);
    } catch (err) {
      console.error('[DailyDecision] share fetch error:', err);
    }
  };

  const handleReportDaily = async () => {
    if (!authUser?.id || !userId || !decision?.id) {
      onRequireAuth?.();
      return;
    }
    setShowReportModal(true);
  };

  const handleSubmitDailyReport = async () => {
    if (!authUser?.id || !userId || !decision?.id || reportSubmitting) return;
    try {
      setReportSubmitting(true);
      const { data, error: reportErr } = await supabase.rpc('submit_report', {
        p_kind: 'daily',
        p_target_id: decision.id,
        p_reason: reportReason,
        p_note: reportNote?.trim() || null,
      });
      if (reportErr) throw reportErr;
      if (!data?.ok) {
        if (data?.error === 'report_duplicate_24h') {
          alert('Already reported. Thanks.');
          setShowReportModal(false);
          return;
        }
        if (data?.error === 'rate_limited') {
          throw new Error('Report limit reached (5 per 24 hours). Try again later.');
        }
        if (data?.error === 'report_rate_limited') {
          throw new Error('You’ve hit the report limit (5/day).');
        }
        throw new Error(data?.error || 'report_submit_failed');
      }
      setShowReportModal(false);
      setReportNote('');
      setReportReason('spam');
      alert('Thanks — report submitted.');
    } catch (err) {
      console.error('[daily] report_submit_failed', {
        decision_id: decision?.id || null,
        message: err?.message || String(err),
      });
      alert(`Could not submit report: ${err?.message || 'unknown_error'}`);
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleRetryNow = async () => {
    if (!decision?.id) return;
    await loadDecision();
  };

  const options = decision?.options || [];
  const topTwo = options.slice(0, 2);
  const showVoteButtons = isOpen && viewMode === 'vote';
  const canonicalDailyAssetUrl = getCanonicalRenderedVerdictAsset({ ...(result || {}), ...(decision || {}) }).url;

  useEffect(() => {
    if (!decision?.id) return;
    console.log('[daily_asset_selection]', {
      decision_id: decision.id,
      meme_status: result?.meme_status || null,
      meme_image_url: result?.meme_image_url || null,
      share_card_url: result?.share_card_url || null,
      final_image_url: result?.final_image_url || null,
      background_image_url: result?.background_image_url || null,
      media_asset_url: result?.media_asset_url || null,
      selected_asset_url: canonicalDailyAssetUrl || null,
      screen: 'daily-vote',
    });
  }, [decision?.id, result?.meme_status, result?.meme_image_url, result?.share_card_url, result?.final_image_url, result?.background_image_url, result?.media_asset_url, canonicalDailyAssetUrl]);

  useEffect(() => {
    if (!decision?.id) return;
    const logPayload = {
      decision_id: decision.id,
      status: decision.status || null,
      is_open: isOpen,
      verdict_ready: verdictReady,
      has_verdict_model: Boolean(verdictPayload),
      total_votes: Number(verdictPayload?.total_votes ?? totalVotes ?? 0),
      winner_index: verdictPayload?.winning_option_index ?? null,
      options_count: options.length,
      meme_status: result?.meme_status || null,
      has_meme_image: Boolean(canonicalDailyAssetUrl),
    };
    const key = JSON.stringify(logPayload);
    if (lastRenderLogKeyRef.current === key) return;
    lastRenderLogKeyRef.current = key;
  }, [decision?.id, decision?.status, isOpen, verdictReady, verdictPayload, totalVotes, options.length, result?.meme_status, canonicalDailyAssetUrl]);

  useEffect(() => {
    if (!decision?.id || !verdictReady) return;
    if (canonicalDailyAssetUrl) {
      memeRefreshAttemptsRef.current = 0;
      if (memeRefreshTimerRef.current) {
        clearTimeout(memeRefreshTimerRef.current);
        memeRefreshTimerRef.current = null;
      }
      return;
    }
    if (memeRefreshAttemptsRef.current >= 5) return;

    if (memeRefreshTimerRef.current) clearTimeout(memeRefreshTimerRef.current);
    const delayMs = 3000 + (memeRefreshAttemptsRef.current * 2000);
    memeRefreshTimerRef.current = setTimeout(async () => {
      memeRefreshAttemptsRef.current += 1;
      await loadResult(decision.id);
    }, delayMs);

    return () => {
      if (memeRefreshTimerRef.current) {
        clearTimeout(memeRefreshTimerRef.current);
        memeRefreshTimerRef.current = null;
      }
    };
  }, [decision?.id, verdictReady, canonicalDailyAssetUrl, loadResult]);

  if (loading && !decision) {
    return (
      <div
        // [DAILY_NATIVE_INITIAL_MOUNT]
        className={`${homeHeroMode ? 'mb-0 rounded-3xl border-slate-800/70 bg-gradient-to-b from-slate-900/90 via-slate-900/85 to-slate-950/80 p-4 sm:p-5 shadow-[0_12px_36px_rgba(0,0,0,0.16)]' : 'mb-5 rounded-2xl border border-purple-500/30 bg-gradient-to-r from-slate-800 to-slate-900 p-4'} border ${isNativeInitialMount ? 'motion-reduce:transition-none' : ''}`}
        style={isNativeInitialMount ? { transition: 'none', animation: 'none' } : undefined}
      >
        <div className="text-sm text-slate-300">Loading Daily Decision…</div>
      </div>
    );
  }

  if (!decision) {
    return (
      <div className={`${homeHeroMode ? 'mb-0 rounded-3xl border-slate-800/70 bg-gradient-to-b from-slate-900/90 via-slate-900/85 to-slate-950/80 p-4 sm:p-5 shadow-[0_12px_36px_rgba(0,0,0,0.16)]' : 'mb-5 rounded-2xl border border-purple-500/30 bg-gradient-to-r from-slate-800 to-slate-900 p-4'} border`}>
        <div className="text-xs uppercase tracking-wide text-purple-300 font-bold">Daily Decision</div>
        <div className="mt-2 text-sm text-slate-300">
          We couldn't load today&apos;s decision yet.
        </div>
        <button
          onClick={handleRetryNow}
          className="mt-3 rounded-lg bg-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`${homeHeroMode ? 'mb-0 rounded-3xl border-slate-800/70 bg-gradient-to-b from-slate-900/90 via-slate-900/85 to-slate-950/80 p-4 sm:p-5 shadow-[0_12px_36px_rgba(0,0,0,0.16)]' : 'mb-5 rounded-2xl border border-purple-500/30 bg-gradient-to-r from-slate-800 to-slate-900 p-4'} border`}>
      {/* [DAILY_DECISION_TOAST] */}
      {showVerdictToast && !homeHeroMode && (
        <div className="mb-3 rounded-xl border border-green-500/40 bg-green-900/20 px-3 py-2 text-xs font-bold text-green-200">
          Verdict is in. Tap to see the results.
        </div>
      )}
      {shareToast && !homeHeroMode && (
        <div className="mb-3 rounded-xl border border-emerald-500/40 bg-emerald-900/20 px-3 py-2 text-xs font-bold text-emerald-200">
          {shareToast}
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className={`uppercase tracking-wide text-purple-300 font-bold ${homeHeroMode ? 'text-[11px]' : 'text-xs'}`}>Daily Decision</div>
          <div className={`mt-1 font-black text-white ${homeHeroMode ? 'text-base leading-snug sm:text-lg' : 'text-lg'}`}>{decision.question}</div>
          {crewContext?.name && (
            <div className="mt-2 inline-flex items-center rounded-full border border-emerald-500/40 bg-emerald-900/20 px-2 py-0.5 text-[10px] font-bold text-emerald-200">
              Crew: {crewContext.name}
            </div>
          )}
          <div className={`mt-1 text-slate-400 ${homeHeroMode ? 'text-[11px]' : 'text-xs'}`}>
            Closes in {countdown}
            <span className="text-slate-500"> • {closesLabel}</span>
          </div>
          {lastUpdatedAt && !homeHeroMode && (
            <div className="text-[10px] text-slate-500 mt-1">Updated {formatLastUpdated(lastUpdatedAt)}</div>
          )}
        </div>
        {!homeHeroMode && (
          <button
            onClick={handleReportDaily}
            disabled={reportSubmitting}
            className="shrink-0 rounded-lg border border-red-700 bg-red-900/20 px-3 py-1.5 text-xs font-semibold text-red-200 hover:bg-red-900/30 disabled:opacity-60"
          >
            {reportSubmitting ? 'Reporting…' : 'Report'}
          </button>
        )}
      </div>
      {!homeHeroMode && (
        <div className="mt-2">
          <CommunityRulesPanel title="Community Rules" compact />
        </div>
      )}
      <ReportModal
        open={showReportModal}
        title="Report Daily Decision"
        reason={reportReason}
        note={reportNote}
        submitting={reportSubmitting}
        onReasonChange={setReportReason}
        onNoteChange={setReportNote}
        onCancel={() => setShowReportModal(false)}
        onSubmit={handleSubmitDailyReport}
      />

      {error && <div className="mt-2 text-xs text-amber-300">{error}</div>}

      {verdictReady && verdictPayload && (
        <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-900/20 p-3">
          <div className="text-sm font-bold text-emerald-200">Final Verdict</div>
          <div className="mt-1 text-sm text-slate-100">{verdictPayload.verdict_text}</div>
          <div className="mt-2 space-y-2">
            {options.map((opt, idx) => {
              const count = Number(verdictPayload?.tallies?.[idx] ?? 0);
              const votesForPct = Number(verdictPayload?.total_votes ?? 0);
              const pct = votesForPct ? Math.round((count / votesForPct) * 100) : 0;
              return (
                <div key={`summary-${idx}`}>
                  <div className="flex items-center justify-between text-xs text-slate-300">
                    <span>{opt}</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-700">
                    <div className="h-2 rounded-full bg-emerald-400" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-2 text-xs text-slate-300">Total votes: {Number(verdictPayload?.total_votes ?? 0)}</div>
        </div>
      )}

      {showVoteButtons ? (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {options.map((opt, idx) => (
            <button
              key={`opt-${idx}`}
              onClick={() => handleVote(idx)}
              disabled={voteSubmitting}
              className={`rounded-lg px-4 py-2 text-sm font-bold text-white transition-all ${
                userVote === idx
                  ? 'bg-green-600 border border-green-400/50'
                  : 'bg-purple-600 hover:bg-purple-500'
              } ${voteSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {opt}
            </button>
          ))}
        </div>
      ) : isOpen ? (
        <div className="mt-3">
          {/* [DAILY_DECISION_LEANING_VIEW] */}
          <div className="text-sm font-bold text-purple-200">Jury leaning</div>
          {tally && (
            <div className="mt-2 space-y-2">
              {options.map((opt, idx) => {
                const count = tally.tallies?.[idx] || 0;
                const pct = totalVotes ? Math.round((count / totalVotes) * 100) : 0;
                return (
                  <div key={`lean-${idx}`}>
                    <div className="flex items-center justify-between text-xs text-slate-300">
                      <span>{opt}</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-700">
                      <div
                        className="h-2 rounded-full bg-purple-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {userVote != null && (
            <div className="mt-2 text-xs text-slate-200">
              You voted: <span className="font-bold">{options[userVote]}</span>
            </div>
          )}
          {/* [DAILY_DECISION_POST_VOTE_PAYOFF] */}
          {userVote != null && totalVotes > 0 && (
            <div className="mt-1 text-xs text-slate-300">
              {(() => {
                const userCount = tally?.tallies?.[userVote] || 0;
                const userPct = totalVotes ? Math.round((userCount / totalVotes) * 100) : 0;
                const entries = Object.entries(tally?.tallies || {}).map(([k, v]) => ({
                  idx: Number(k),
                  count: Number(v)
                }));
                const ranked = entries
                  .filter((e) => Number.isFinite(e.idx))
                  .sort((a, b) => (b.count - a.count) || (a.idx - b.idx));
                const topCount = ranked[0]?.count ?? 0;
                const leaders = ranked.filter((e) => e.count === topCount);
                const isTiedTop = leaders.length > 1 && topCount > 0;
                const userIsLeadingTie = leaders.some((e) => e.idx === userVote);
                if (isTiedTop && userIsLeadingTie) {
                  return `You’re tied for the lead (${userPct}%).`;
                }
                if (isTiedTop && !userIsLeadingTie) {
                  return `You’re trailing (${userPct}%).`;
                }
                const top = ranked[0];
                const inMajority = top && top.idx === userVote;
                return inMajority
                  ? `You’re with the majority (${userPct}%).`
                  : `You’re in the minority (${userPct}%).`;
              })()}
            </div>
          )}
          {userVote != null && totalVotes === 0 && (
            <div className="mt-1 text-xs text-slate-300">
              Vote locked in. You’re first on the board.
            </div>
          )}
          {crewPulse?.ok && (
            <div className="mt-3 rounded-lg border border-slate-700 bg-slate-900 p-3 text-xs text-slate-200">
              <div className="font-bold text-purple-200">Crew Pulse</div>
              <div className="mt-1">
                {crewPulse.total_votes} / {crewPulse.crew_members} voted
                {crewPulse.crew_members != null && (
                  <span className="text-slate-400"> • Votes needed: {Math.max(0, crewPulse.crew_members - (crewPulse.total_votes || 0))}</span>
                )}
              </div>
              <div className="mt-1">A: {crewPulse.option_a_pct}% • B: {crewPulse.option_b_pct}%</div>
            </div>
          )}
          {/* [DAILY_DECISION_CHANGE_VOTE] */}
          <button
            onClick={() => setViewMode('vote')}
            className="mt-3 rounded-lg bg-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-600"
          >
            Change vote
          </button>
        </div>
      ) : verdictReady ? (
        <div className="mt-3">
          {/* [DAILY_DECISION_MEME_DISPLAY] */}
          <div className="rounded-2xl overflow-hidden border border-slate-700 bg-slate-900">
            <VerdictMemeFrame
              id={decision?.id}
              title={decision?.question || 'Daily Decision'}
              sideA={options[0]}
              sideB={options[1]}
              verdictLine={dailyRenderState.text || verdictPayload?.verdict_text || null}
              imageUrl={dailyRenderState.imageUrl || null}
              assetSource={{ ...(result || {}), ...(decision || {}) }}
              winnerLabel={options[verdictPayload?.winning_option_index ?? 0] || 'Winner'}
              loserLabel={options[(verdictPayload?.winning_option_index ?? 0) === 0 ? 1 : 0] || 'Runner-up'}
              isDaily
              heightClass="h-64"
              renderState={dailyRenderState.verdictReady ? 'ready' : (dailyRenderState.verdictFailed ? 'failed' : 'processing')}
              showEmoji={false}
              surface="daily-vote"
            />
            <div className="p-4">
              {dailyRenderState.text && dailyRenderState.imageUrl && (
                <div className="text-slate-100 font-bold italic">"{dailyRenderState.text}"</div>
              )}
              <div className="mt-3 text-xs text-slate-400">Total votes: {Number(verdictPayload?.total_votes ?? totalVotes ?? 0)}</div>
              <div className="mt-3 flex gap-2">
                {/* [DAILY_DECISION_MEME_SHARE] */}
                <button
                  onClick={handleShare}
                  className="flex-1 rounded-lg bg-purple-700 px-4 py-2 text-sm font-bold text-white hover:bg-purple-600"
                >
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : closedPending ? (
        <div className="mt-3">
          <div className="text-sm font-bold text-slate-200">Counting votes...</div>
          <div className="text-xs text-slate-400 mt-1">
            {pendingDelayed
              ? 'Verdict is delayed. We are retrying automatically.'
              : 'Verdict is being prepared. We’ll refresh automatically.'}
          </div>
          <button
            onClick={handleRetryNow}
            className="mt-3 rounded-lg bg-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-600"
          >
            Retry now
          </button>
        </div>
      ) : null}

      {/* [DAILY_QUESTION_SHARE_CTA] */}
      {DAILY_QUESTION_CANONICAL_SHARE ? (
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => setShareModalOpen(true)}
            disabled={allShared || allCrews.length === 0}
            // [DAILY_DECISION_SHARE_BUTTON_COLOR]
            className="flex-1 rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-slate-900 hover:bg-amber-400 disabled:opacity-60"
          >
            {allCrews.length === 0 ? 'No crews yet' : (allShared ? 'Shared ✅' : (sharedCrewIds.size > 0 ? 'Share with Another Crew' : 'Share with Crew'))}
          </button>
        </div>
      ) : null}

      {verdictShareOpen && shareBlob && (
        <AdvancedShareModal
          imageBlob={shareBlob}
          trial={{
            id: decision.id,
            title: decision.question,
            sideA: topTwo[0] || 'Option A',
            sideB: topTwo[1] || 'Option B',
            totalVotes: result?.total_votes || 0
          }}
          verdict={{
            winner: (verdictPayload?.winning_option_index ?? 0) === 0 ? 'A' : 'B',
            winnerPct: verdictPayload?.total_votes
              ? Math.round(((verdictPayload?.tallies?.[verdictPayload?.winning_option_index] || 0) / verdictPayload.total_votes) * 100)
              : 50
          }}
          onClose={() => setVerdictShareOpen(false)}
        />
      )}

      {DAILY_QUESTION_CANONICAL_SHARE && (
        <DailyQuestionShareModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          dailyQuestionId={decision?.id}
          currentUserId={appUserId}
          availableCrews={allCrews}
          sharedCrewIds={sharedCrewIds}
          onShared={(data, sharedCrewNames = []) => {
            const results = data?.perCrew || data?.results || [];
            const counts = data?.totals || data?.counts || {
              shared: results.filter((r) => r.status === 'shared').length,
              already_shared: results.filter((r) => r.status === 'already_shared').length,
              not_member: results.filter((r) => r.status === 'not_member').length,
              failed: results.filter((r) => r.status === 'failed' || r.status === 'error').length,
            };
            setSharedCrewIds((prev) => {
              const next = new Set(prev);
              results.forEach((r) => {
                if (r.status === 'shared' || r.status === 'already_shared') {
                  next.add(r.crew_id);
                }
              });
              return next;
            });
            const newlyShared = results.filter((r) => r.status === 'shared');
            const already = counts.already_shared || 0;
            const failed = (counts.failed || 0) + (counts.not_member || 0);
            const lines = [];
            if (newlyShared.length === 1) {
              const crewName = sharedCrewNames[0] || 'crew';
              lines.push(`Shared with ${crewName} ✅`);
            } else if (newlyShared.length > 1) {
              lines.push(`Shared to ${newlyShared.length} crews ✅`);
            }
            if (already > 0) lines.push(`${already} already shared`);
            if (failed > 0) lines.push(`${failed} failed`);
            if (lines.length) {
              setShareToast(lines.join(' • '));
              setTimeout(() => setShareToast(null), 4000);
            }
            console.log('[daily] share_submit_result', { decision_id: decision?.id, results });
            if (decision?.id) {
              loadShareState(decision.id, appUserId, authUser?.id);
            }
          }}
        />
      )}
    </div>
  );
}
